import { useState, useEffect, useRef } from 'react';
import { useQuery, useMutation } from '@tanstack/react-query';
import { useAuthStore } from '../stores/auth';
import { api, socket } from '../utils/api';
import { cn } from '../utils/cn';
import {
  Hash,
  Send,
  Search,
  Settings,
  Plus,
  Smile,
  Image,
  AtSign,
  TrendingUp,
  Bot,
  Zap,
  HelpCircle,
  Users,
  ChevronDown,
  MoreHorizontal,
} from 'lucide-react';

interface Message {
  id: string;
  userId: string;
  username: string;
  content: string;
  channel: string;
  timestamp: string;
  reactions?: Record<string, string[]>;
}

interface Channel {
  id: string;
  name: string;
  description?: string;
  icon: typeof Hash;
  memberCount: number;
  unreadCount?: number;
}

const DEFAULT_CHANNELS: Channel[] = [
  { id: 'general', name: 'General', description: 'General discussion', icon: Hash, memberCount: 0 },
  { id: 'trading', name: 'Trading', description: 'Trading strategies and tips', icon: TrendingUp, memberCount: 0 },
  { id: 'bots', name: 'Bots', description: 'Bot development and automation', icon: Bot, memberCount: 0 },
  { id: 'signals', name: 'Signals', description: 'Trading signals and alerts', icon: Zap, memberCount: 0 },
  { id: 'support', name: 'Support', description: 'Get help from the community', icon: HelpCircle, memberCount: 0 },
];

export default function Chat() {
  const { user } = useAuthStore();
  const messagesEndRef = useRef<HTMLDivElement>(null);
  
  const [activeChannel, setActiveChannel] = useState('general');
  const [messageInput, setMessageInput] = useState('');
  const [messages, setMessages] = useState<Message[]>([]);
  const [onlineUsers, setOnlineUsers] = useState<string[]>([]);
  const [showMembers, setShowMembers] = useState(true);
  
  // Fetch messages for active channel
  const { data: channelMessages } = useQuery<Message[]>({
    queryKey: ['messages', activeChannel],
    queryFn: () => api.get(`/chat/messages/${activeChannel}?limit=100`).then((r: any) => r.data),
  });
  
  // Update messages when fetched
  useEffect(() => {
    if (channelMessages) {
      setMessages(channelMessages);
    }
  }, [channelMessages]);
  
  // Socket connection for real-time messages
  useEffect(() => {
    socket.emit('chat:join', { channel: activeChannel });
    
    socket.on('chat:message', (message: Message) => {
      if (message.channel === activeChannel) {
        setMessages((prev) => [...prev, message]);
      }
    });
    
    socket.on('chat:users', (users: string[]) => {
      setOnlineUsers(users);
    });
    
    return () => {
      socket.emit('chat:leave', { channel: activeChannel });
      socket.off('chat:message');
      socket.off('chat:users');
    };
  }, [activeChannel]);
  
  // Auto-scroll to bottom
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);
  
  // Send message mutation
  const sendMessageMutation = useMutation({
    mutationFn: (content: string) =>
      api.post('/chat/messages', { channel: activeChannel, content }),
    onSuccess: () => {
      setMessageInput('');
    },
  });
  
  const handleSendMessage = (e: React.FormEvent) => {
    e.preventDefault();
    if (!messageInput.trim()) return;
    
    const tempMessage: Message = {
      id: `temp-${Date.now()}`,
      userId: user?.id || '',
      username: user?.email?.split('@')[0] || 'Anonymous',
      content: messageInput,
      channel: activeChannel,
      timestamp: new Date().toISOString(),
    };
    
    setMessages((prev) => [...prev, tempMessage]);
    sendMessageMutation.mutate(messageInput);
  };
  
  const formatTime = (timestamp: string) => {
    const date = new Date(timestamp);
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };
  
  const formatDate = (timestamp: string) => {
    const date = new Date(timestamp);
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);
    
    if (date.toDateString() === today.toDateString()) return 'Today';
    if (date.toDateString() === yesterday.toDateString()) return 'Yesterday';
    return date.toLocaleDateString();
  };
  
  const groupedMessages = messages.reduce((groups, message) => {
    const date = formatDate(message.timestamp);
    if (!groups[date]) groups[date] = [];
    groups[date].push(message);
    return groups;
  }, {} as Record<string, Message[]>);

  const activeChannelData = DEFAULT_CHANNELS.find((c) => c.id === activeChannel);
  
  return (
    <div className="flex h-full">
      {/* Channels sidebar */}
      <div className="w-64 border-r border-white/[0.06] bg-[#12161c] flex flex-col">
        <div className="p-4 border-b border-white/[0.06]">
          <div className="flex items-center justify-between mb-3">
            <h2 className="font-semibold text-white">Community</h2>
            <button className="p-1.5 rounded-lg text-[#848e9c] hover:text-white hover:bg-white/[0.06] transition-colors">
              <ChevronDown className="h-4 w-4" />
            </button>
          </div>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#5e6673]" />
            <input
              type="text"
              placeholder="Search channels..."
              className="w-full bg-[#181a20] border border-white/[0.08] rounded-lg pl-9 pr-3 py-2 text-sm text-white placeholder-[#5e6673] focus:outline-none focus:border-[#f0b90b]/50"
            />
          </div>
        </div>
        
        <div className="flex-1 overflow-y-auto no-scrollbar p-2">
          <div className="px-2 py-1.5 text-xs font-medium text-[#5e6673] uppercase tracking-wider">
            Channels
          </div>
          {DEFAULT_CHANNELS.map((channel) => {
            const Icon = channel.icon;
            return (
              <button
                key={channel.id}
                onClick={() => setActiveChannel(channel.id)}
                className={cn(
                  'w-full flex items-center gap-2 px-3 py-2 rounded-lg mb-0.5 transition-all',
                  activeChannel === channel.id
                    ? 'bg-[#f0b90b]/10 text-[#f0b90b]'
                    : 'text-[#848e9c] hover:bg-white/[0.04] hover:text-white'
                )}
              >
                <Icon className="h-4 w-4" />
                <span className="flex-1 text-left text-sm">{channel.name}</span>
                {channel.unreadCount && channel.unreadCount > 0 && (
                  <span className="bg-[#f6465d] text-white text-xs px-1.5 py-0.5 rounded-full min-w-[18px] text-center">
                    {channel.unreadCount}
                  </span>
                )}
              </button>
            );
          })}
        </div>
        
        {/* Online users preview */}
        <div className="border-t border-white/[0.06] p-4">
          <div className="flex items-center justify-between mb-3">
            <div className="text-xs font-medium text-[#5e6673] uppercase tracking-wider">
              Online — {onlineUsers.length}
            </div>
            <div className="flex -space-x-2">
              {onlineUsers.slice(0, 3).map((username, i) => (
                <div
                  key={username}
                  className="w-6 h-6 rounded-full bg-gradient-to-br from-[#f0b90b] to-[#d9a60a] flex items-center justify-center text-[10px] font-bold text-[#0b0e11] border-2 border-[#12161c]"
                  style={{ zIndex: 3 - i }}
                >
                  {username[0].toUpperCase()}
                </div>
              ))}
              {onlineUsers.length > 3 && (
                <div className="w-6 h-6 rounded-full bg-[#2b3139] flex items-center justify-center text-[10px] font-medium text-[#848e9c] border-2 border-[#12161c]">
                  +{onlineUsers.length - 3}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
      
      {/* Chat area */}
      <div className="flex-1 flex flex-col bg-[#0b0e11]">
        {/* Channel header */}
        <div className="h-14 px-4 border-b border-white/[0.06] flex items-center justify-between bg-[#12161c]/50">
          <div className="flex items-center gap-3">
            {activeChannelData && (
              <>
                <div className="w-8 h-8 rounded-lg bg-[#f0b90b]/10 flex items-center justify-center">
                  <activeChannelData.icon className="h-4 w-4 text-[#f0b90b]" />
                </div>
                <div>
                  <h2 className="font-semibold text-white">{activeChannelData.name}</h2>
                  <p className="text-xs text-[#848e9c]">{activeChannelData.description}</p>
                </div>
              </>
            )}
          </div>
          <div className="flex items-center gap-2">
            <button className="p-2 rounded-lg text-[#848e9c] hover:text-white hover:bg-white/[0.06] transition-colors">
              <Search className="h-5 w-5" />
            </button>
            <button
              onClick={() => setShowMembers(!showMembers)}
              className={cn(
                "p-2 rounded-lg transition-colors",
                showMembers 
                  ? "text-[#f0b90b] bg-[#f0b90b]/10" 
                  : "text-[#848e9c] hover:text-white hover:bg-white/[0.06]"
              )}
            >
              <Users className="h-5 w-5" />
            </button>
          </div>
        </div>
        
        {/* Messages area */}
        <div className="flex-1 flex">
          <div className="flex-1 overflow-y-auto no-scrollbar p-4">
            {Object.entries(groupedMessages).map(([date, dateMessages]) => (
              <div key={date}>
                {/* Date divider */}
                <div className="flex items-center justify-center my-6">
                  <div className="flex-1 border-t border-white/[0.06]" />
                  <span className="px-4 py-1 text-xs font-medium text-[#5e6673] bg-[#12161c] rounded-full">
                    {date}
                  </span>
                  <div className="flex-1 border-t border-white/[0.06]" />
                </div>
                
                {/* Messages */}
                {dateMessages.map((message, index) => {
                  const showAvatar = index === 0 || 
                    dateMessages[index - 1].userId !== message.userId;
                  
                  return (
                    <div
                      key={message.id}
                      className={cn('flex group', showAvatar ? 'mt-4' : 'mt-0.5')}
                    >
                      {showAvatar ? (
                        <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#f0b90b] to-[#d9a60a] flex items-center justify-center text-[#0b0e11] font-bold mr-3 flex-shrink-0">
                          {message.username[0].toUpperCase()}
                        </div>
                      ) : (
                        <div className="w-10 mr-3 flex-shrink-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                          <span className="text-[10px] text-[#5e6673]">
                            {formatTime(message.timestamp)}
                          </span>
                        </div>
                      )}
                      
                      <div className="flex-1 min-w-0">
                        {showAvatar && (
                          <div className="flex items-baseline gap-2 mb-1">
                            <span className="font-semibold text-white hover:underline cursor-pointer">
                              {message.username}
                            </span>
                            <span className="text-xs text-[#5e6673]">
                              {formatTime(message.timestamp)}
                            </span>
                          </div>
                        )}
                        <p className="text-[#eaecef] break-words leading-relaxed">{message.content}</p>
                      </div>
                      
                      {/* Message actions */}
                      <div className="opacity-0 group-hover:opacity-100 transition-opacity flex items-start gap-1 ml-2">
                        <button className="p-1 rounded text-[#5e6673] hover:text-white hover:bg-white/[0.06]">
                          <Smile className="h-4 w-4" />
                        </button>
                        <button className="p-1 rounded text-[#5e6673] hover:text-white hover:bg-white/[0.06]">
                          <MoreHorizontal className="h-4 w-4" />
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            ))}
            
            {messages.length === 0 && (
              <div className="text-center py-20">
                <div className="w-16 h-16 rounded-2xl bg-[#1e2329] flex items-center justify-center mx-auto mb-4">
                  <Hash className="h-8 w-8 text-[#848e9c]" />
                </div>
                <h3 className="text-lg font-semibold text-white mb-2">
                  Welcome to #{activeChannelData?.name}
                </h3>
                <p className="text-[#848e9c]">This is the start of the channel. Say hello!</p>
              </div>
            )}
            
            <div ref={messagesEndRef} />
          </div>
          
          {/* Members sidebar */}
          {showMembers && (
            <div className="w-60 border-l border-white/[0.06] bg-[#12161c]/50 overflow-y-auto no-scrollbar">
              <div className="p-4">
                <div className="text-xs font-medium text-[#5e6673] uppercase tracking-wider mb-3">
                  Online — {onlineUsers.length}
                </div>
                <div className="space-y-1">
                  {onlineUsers.map((username) => (
                    <div key={username} className="flex items-center gap-3 px-2 py-1.5 rounded-lg hover:bg-white/[0.04] cursor-pointer">
                      <div className="relative">
                        <div className="w-8 h-8 rounded-full bg-gradient-to-br from-[#8b5cf6] to-[#6366f1] flex items-center justify-center text-white font-bold text-sm">
                          {username[0].toUpperCase()}
                        </div>
                        <div className="absolute -bottom-0.5 -right-0.5 w-3 h-3 bg-[#0ecb81] rounded-full border-2 border-[#12161c]" />
                      </div>
                      <span className="text-sm text-[#eaecef] truncate">{username}</span>
                    </div>
                  ))}
                </div>
                
                {onlineUsers.length === 0 && (
                  <div className="text-center py-6">
                    <p className="text-sm text-[#5e6673]">No users online</p>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
        
        {/* Message input */}
        <div className="p-4 border-t border-white/[0.06]">
          <form onSubmit={handleSendMessage} className="relative">
            <div className="flex items-center gap-2 bg-[#1e2329] rounded-xl border border-white/[0.08] focus-within:border-[#f0b90b]/50 transition-colors">
              <button
                type="button"
                className="p-3 text-[#5e6673] hover:text-[#848e9c] transition-colors"
              >
                <Plus className="h-5 w-5" />
              </button>
              
              <input
                type="text"
                value={messageInput}
                onChange={(e) => setMessageInput(e.target.value)}
                placeholder={`Message #${activeChannelData?.name || 'channel'}`}
                className="flex-1 bg-transparent py-3 text-white placeholder-[#5e6673] focus:outline-none"
              />
              
              <div className="flex items-center gap-1 pr-2">
                <button
                  type="button"
                  className="p-2 text-[#5e6673] hover:text-[#848e9c] transition-colors"
                >
                  <Image className="h-5 w-5" />
                </button>
                <button
                  type="button"
                  className="p-2 text-[#5e6673] hover:text-[#848e9c] transition-colors"
                >
                  <Smile className="h-5 w-5" />
                </button>
                <button
                  type="submit"
                  disabled={!messageInput.trim()}
                  className={cn(
                    "p-2 rounded-lg transition-colors",
                    messageInput.trim()
                      ? "text-[#f0b90b] hover:bg-[#f0b90b]/10"
                      : "text-[#5e6673] cursor-not-allowed"
                  )}
                >
                  <Send className="h-5 w-5" />
                </button>
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
